import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import { get, put, del } from '@/lib/api'

// ==================== Types ====================

export type ViewType = 'workflow_tasks' | 'team_workload' | 'project_issues'

export interface ViewPreferences {
  groupBy?: string
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
  defaultFilter?: string
  pageSize?: number
}

export interface ViewPreferencesResult {
  id: string
  userId: string
  viewType: ViewType
  preferences: ViewPreferences
  createdAt: string
  updatedAt: string | null
}

// ==================== Query Keys ====================

export const viewPreferencesKeys = {
  all: ['view-preferences'] as const,
  byType: (viewType: ViewType) => [...viewPreferencesKeys.all, viewType] as const,
  allPreferences: () => [...viewPreferencesKeys.all, 'all'] as const,
}

// ==================== Hooks ====================

/**
 * 获取视图偏好
 * Requirements: 6.7
 */
export function useViewPreferences(viewType: ViewType) {
  return useQuery({
    queryKey: viewPreferencesKeys.byType(viewType),
    queryFn: () => get<ViewPreferencesResult | null>(`/pm/users/view-preferences/${viewType}`),
  })
}

/**
 * 获取所有视图偏好
 * Requirements: 6.7
 */
export function useAllViewPreferences() {
  return useQuery({
    queryKey: viewPreferencesKeys.allPreferences(),
    queryFn: () => get<ViewPreferencesResult[]>('/pm/users/view-preferences'),
  })
}

/**
 * 保存视图偏好
 * Requirements: 6.7
 */
export function useSaveViewPreferences() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ viewType, preferences }: { viewType: ViewType; preferences: ViewPreferences }) =>
      put<ViewPreferencesResult>(`/pm/users/view-preferences/${viewType}`, preferences),
    onSuccess: (_, { viewType }) => {
      void queryClient.invalidateQueries({ queryKey: viewPreferencesKeys.byType(viewType) })
      void queryClient.invalidateQueries({ queryKey: viewPreferencesKeys.allPreferences() })
    },
  })
}

/**
 * 删除视图偏好
 * Requirements: 6.7
 */
export function useDeleteViewPreferences() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (viewType: ViewType) => del<null>(`/pm/users/view-preferences/${viewType}`),
    onSuccess: (_, viewType) => {
      void queryClient.invalidateQueries({ queryKey: viewPreferencesKeys.byType(viewType) })
      void queryClient.invalidateQueries({ queryKey: viewPreferencesKeys.allPreferences() })
    },
  })
}
